import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogoIcon } from "../components/Icons/Logo";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, OIDC_DEFAULT_SCOPES } from "@azure/msal-browser";
import { AuthContext } from "../context/AuthContext";
import { useAlert } from "../context/AlertContext";
import { useTranslation } from "../i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher/LanguageSwitcher";

export const Welcome = () => {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { setAlert } = useAlert();
  const { t, lang } = useTranslation();

  const currentUser = authContext?.currentUser;
  const needsRegistration = authContext?.needsRegistration ?? false;
  const loading = authContext?.loading ?? false;
  const isAuthenticated = authContext?.isAuthenticated ?? false;

  const handleLogin = async () => {
    try {
      // The External ID sign-in page ignores ui_locales and only follows mkt.
      // ui_locales is the standard OIDC parameter, so it is sent as well in
      // case the page starts honouring it or the identity provider changes.
      await instance.loginRedirect({
        scopes: OIDC_DEFAULT_SCOPES,
        extraQueryParameters: { ui_locales: lang, mkt: lang },
      });
    } catch (error) {
      console.error("Login failed:", error);
      setAlert(
        true,
        "error",
        t("welcome.signInFailed"),
      );
    }
  };

  useEffect(() => {
    if (needsRegistration) {
      navigate("/register");
      return;
    }
    if (!currentUser) return;

    const { userType, userData } = currentUser;

    const paths: Record<string, string> = {
      organisation:
        ((userData as any)?.step_of_registration ?? 0) >= 2
          ? "/organisation/events"
          : "/register/organisation",
      volunteer:
        ((userData as any)?.step_of_registration ?? 0) >= 3
          ? "/volunteer/events"
          : "/register/volunteer",
    };

    const path = paths[userType];

    if (path) navigate(path);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, needsRegistration]);

  const msalBusy = inProgress !== InteractionStatus.None || isAuthenticated;

  return (
    <Box sx={{ minHeight: "100vh", padding: "80px" }}>
      <Box
        sx={{
          marginBottom: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <LogoIcon />
        <LanguageSwitcher />
      </Box>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        {loading || msalBusy ? (
          <CircularProgress />
        ) : (
          <>
            <Typography component="h1" variant="h3" gutterBottom>
              {t("welcome.title")}
            </Typography>
            <Typography
              component="h2"
              variant="h5"
              sx={{ textAlign: "center" }}
            >
              {t("welcome.subtitle")}
            </Typography>
            <Typography
              component="h2"
              variant="h6"
              sx={{ textAlign: "center" }}
            >
              {t("welcome.text")}
            </Typography>
            <Typography
              component="h2"
              variant="h6"
              gutterBottom
              sx={{ textAlign: "center" }}
            >
              {t("welcome.closing")}
            </Typography>

            <Button
              variant="contained"
              size="medium"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
            >
              {t("welcome.enter")}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
